import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Box,
  Text,
  Stack,
  Flex,
  Group,
  Button,
  Checkbox,
  Divider,
  TextInput,
  Collapse,
  UnstyledButton,
} from '@mantine/core';
import { IconX, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import AggregatedReportsAPI from '../../api/AggregatedReportsApi';
import { VisiLoader } from '../GeneralComponent/GeneralComponent';

type ResetCampaign = {
  campaign_id: string;
  campaign_name: string;
  used_in_count: number;
  used_in_report: string[];
};

type AggregateResetStatsProps = {
  opened: boolean;
  onClose: () => void;
  reportId: string;
  campaignIds: string[];
  onResetDone: (message: string) => void;
};

export function AggregateResetStats({
  opened,
  onClose,
  reportId,
  campaignIds,
  onResetDone,
}: AggregateResetStatsProps) {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<ResetCampaign[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string>('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Nothing is pre-selected, even for a single-campaign report.
  useEffect(() => {
    if (!opened) { return; }
    setCampaigns([]);
    setSelectedIds([]);
    setExpandedId('');
    setConfirmText('');
    if (campaignIds.length === 0) { return; }
    setLoading(true);
    AggregatedReportsAPI
      .getresetcampaigns({ report_id: reportId, visiopt_campaign_id: campaignIds })
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        if (data && data.status === 1 && data.data) {
          setCampaigns(Array.isArray(data.data.campaigns) ? data.data.campaigns : []);
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const allSelected = campaigns.length > 0 && selectedIds.length === campaigns.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () =>
    setSelectedIds(allSelected ? [] : campaigns.map((c) => c.campaign_id));

  const toggleOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const canReset = selectedIds.length > 0 && confirmText === 'RESET' && !resetting;

  const handleReset = () => {
    if (!canReset) { return; }
    setResetting(true);
    AggregatedReportsAPI
      .resetstats({ visiopt_campaign_id: selectedIds, confirm_text: confirmText })
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        if (data && data.status === 1) {
          onClose();
          onResetDone(data.message);
        } else {
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: (data && data.message) || 'Could not reset the stats.',
          });
        }
      })
      .catch((error) => {
        console.log(error);
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: 'Could not reset the stats.',
        });
      })
      .finally(() => setResetting(false));
  };

  return (
    <Modal
      opened={opened}
      onClose={() => { if (!resetting) { onClose(); } }}
      title="Reset Stats"
      centered
      size="lg"
      trapFocus={false}
    >
      <VisiLoader VisiLoaderVisible={loading || resetting} />
      <Stack spacing="md">
        <Box>
          <Text size="sm">
            This permanently deletes conversion and traffic data for the campaigns you select. It
            cannot be undone.
          </Text>
          <Text size="sm" mt={6}>
            These campaigns are used in other reports. Resetting here removes the data everywhere.
          </Text>
        </Box>

        <Divider />

        <Box>
          <Checkbox
            label="Select all"
            color="red"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={toggleAll}
            disabled={campaigns.length === 0}
          />
          <Divider my="xs" />
          <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
            <Stack spacing="xs">
              {campaigns.length === 0 ? (
                <Text size="sm" color="dimmed" py="sm">
                  No Visiopt campaign in this report.
                </Text>
              ) : (
                campaigns.map((c) => {
                  const expanded = expandedId === c.campaign_id;
                  return (
                    <Box key={c.campaign_id}>
                      <Flex align="center" justify="space-between" gap="sm">
                        <Checkbox
                          color="red"
                          checked={selectedIds.includes(c.campaign_id)}
                          onChange={() => toggleOne(c.campaign_id)}
                          label={c.campaign_name}
                        />
                        {c.used_in_count > 0 ? (
                          <UnstyledButton
                            onClick={() => setExpandedId(expanded ? '' : c.campaign_id)}
                          >
                            <Flex align="center" gap={4}>
                              <Text size="xs" color="dimmed">
                                Used in {c.used_in_count} other report
                                {c.used_in_count === 1 ? '' : 's'}
                              </Text>
                              {expanded ? (
                                <IconChevronUp size="0.8rem" />
                              ) : (
                                <IconChevronDown size="0.8rem" />
                              )}
                            </Flex>
                          </UnstyledButton>
                        ) : (
                          <Text size="xs" color="dimmed">
                            Not used in other reports
                          </Text>
                        )}
                      </Flex>
                      <Collapse in={expanded}>
                        <Stack spacing={2} pl={30} pt={4}>
                          {c.used_in_report.map((name) => (
                            <Text key={name} size="xs" color="dimmed">
                              {name}
                            </Text>
                          ))}
                        </Stack>
                      </Collapse>
                    </Box>
                  );
                })
              )}
            </Stack>
          </Box>
        </Box>

        <TextInput
          label="Type RESET to confirm"
          value={confirmText}
          onChange={(e) => setConfirmText(e.currentTarget.value)}
        />

        <Group position="apart" mt="xs">
          <Button variant="default" onClick={onClose} disabled={resetting}>
            Cancel
          </Button>
          <Button color="red" onClick={handleReset} disabled={!canReset} loading={resetting}>
            Reset Stats
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
